import axios from '../../axios'

const state = {
  plans: [],
  pagination: {
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
  },
  stats: { active: 0, overdue: 0, completed: 0, upcoming: 0 },
  statsLoading: false,
  loading: false,
  paymentMethods: [],
  pendingSchedules: [],
  submitting: false,
}

const actions = {
  async fetchPlans({ commit }, filters) {
    commit('SET_LOADING', true)
    try {
      const params = { ...filters }
      const { data } = await axios.get('/user/student/installment-plans', { params })
      commit('SET_PLANS', data.data || [])
      commit('SET_META', data.meta || {})
    } catch {
      commit('SET_PLANS', [])
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchStats({ commit }) {
    commit('SET_STATS_LOADING', true)
    try {
      const { data } = await axios.get('/user/student/installment-stats')
      commit('SET_STATS', data.data || {})
    } catch { /* ignore */ } finally {
      commit('SET_STATS_LOADING', false)
    }
  },

  async fetchPaymentMethods({ commit }) {
    try {
      const { data } = await axios.get('/get-data/payment-methods')
      const list = data?.data || data || []
      commit('SET_PAYMENT_METHODS', Array.isArray(list) ? list : [])
    } catch { /* ignore */ }
  },

  async fetchSchedules({ commit }, planId) {
    try {
      const { data } = await axios.get(`/user/student/installment-plans/${planId}/schedules`)
      commit('SET_PENDING_SCHEDULES', data.data || [])
    } catch {
      commit('SET_PENDING_SCHEDULES', [])
    }
  },

  /** Returns all schedules (paid + pending + overdue) for full history display */
  async fetchAllSchedules(_, planId) {
    try {
      const { data } = await axios.get(`/user/student/installment-plans/${planId}/schedules`)
      return data.data || []
    } catch {
      return []
    }
  },

  async submitPayment({ commit }, payload) {
    commit('SET_SUBMITTING', true)
    try {
      await axios.post('/user/student/installment-payments', payload)
      return { success: true }
    } catch (e) {
      const msg = e.response?.data?.message || 'Failed to record payment.'
      return { success: false, message: msg }
    } finally {
      commit('SET_SUBMITTING', false)
    }
  },

  async fetchDashboardStats() {
    try {
      const { data } = await axios.get('/user/student/installment-plans', {
        params: { per_page: 1000 },
      })
      const rows = data.data || []
      return {
        active: rows.filter(r => r.status === 'active' && r.overdue_count === 0).length,
        overdue: rows.filter(r => r.overdue_count > 0).length,
        completed: rows.filter(r => r.status === 'completed').length,
      }
    } catch {
      return null
    }
  },

  async fetchStudentSummary(_, studentId) {
    try {
      const { data } = await axios.get(`/user/student/installment-summary/${studentId}`)
      return data.data ?? null
    } catch {
      return null
    }
  },

  async fetchPlanByDeal(_, dealId) {
    try {
      const { data } = await axios.get('/user/student/installment-plans', {
        params: { 'filter[student_deal_id]': dealId, per_page: 1 },
      })
      return data.data?.[0] || null
    } catch {
      return null
    }
  },

  async fetchPlanByStudent(_, studentId) {
    try {
      const { data } = await axios.get('/user/student/installment-plans', {
        params: { 'filter[student_id]': studentId, 'filter[status]': 'active', per_page: 1 },
      })
      return data.data?.[0] || null
    } catch {
      return null
    }
  },

  async fetchActivePlans(_, studentId) {
    try {
      const { data } = await axios.get('/user/student/installment-plans', {
        params: { 'filter[student_id]': studentId, 'filter[status]': 'active', per_page: 10 },
      })
      return data.data || []
    } catch {
      return []
    }
  },

  async cancelPlan(_, planId) {
    try {
      await axios.post(`/user/student/installment-plans/${planId}/cancel`)
      return { success: true }
    } catch (e) {
      return { success: false, message: e.response?.data?.message || 'Failed to cancel plan.' }
    }
  },
}

const mutations = {
  SET_PLANS(state, plans) {
    state.plans = plans
  },
  SET_META(state, meta) {
    state.pagination = {
      current_page: meta.current_page || 1,
      per_page: meta.per_page || 20,
      total: meta.total || 0,
      last_page: meta.last_page || 1,
    }
  },
  SET_LOADING(state, val) {
    state.loading = val
  },
  SET_PAYMENT_METHODS(state, methods) {
    state.paymentMethods = methods
  },
  SET_PENDING_SCHEDULES(state, schedules) {
    state.pendingSchedules = schedules
  },
  SET_SUBMITTING(state, val) {
    state.submitting = val
  },
  SET_STATS(state, stats) {
    state.stats = { ...state.stats, ...stats }
  },
  SET_STATS_LOADING(state, val) {
    state.statsLoading = val
  },
}

const getters = {}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
